import { Injectable } from '@angular/core';
import { InvoicePositionApiService } from './index';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { Product } from '@shared/models/product';
import { MatDialog } from '@angular/material/dialog';
import { concatAll, EMPTY, forkJoin, Observable } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { InvoicePosition } from '@shared/models/invoice-position';
import { WinterStorageAssignmentInvoicePositionsComponent } from '@bcm-work-flows/winter-storage-assignment-invoice-positions/winter-storage-assignment-invoice-positions.component';
import { ProduktQuantityButton } from '@shared/models/product-quantity-button';
import { BcmDynamicPriceContext } from '@shared/models/bcm-dynamic-price';
import { WinterStorageBoatAssignment } from '@shared/models/winter-storage-boat-assignment';
import { WinterStorageBillingPayload } from '@bcm-work-flows/winter-storage-assignment-billing-panel/winter-storage-assignment-billing.helper';
import { InvoicePositionDialogComponent } from '@sharedComponents/dialogs/invoice-position-dialog/invoice-position-dialog.component';
import { Boat } from '@shared/models/boat';
import { BerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { BcmCostCenter } from '@shared/models/bcm-cost-center';
import { buildAreaQuantityButtons } from '@shared/functions/build-area-quantity-buttons';
import { applyInvoicePositionBoatForSave, storedInvoicePositionBoat } from '@modules/bcm/@shared/services/invoice-position-boat.util';

@Injectable({providedIn: 'root'})
export class BcmInvoicePositionsService {

    constructor(private _invoicePositionApiService: InvoicePositionApiService,
                private _matDialog: MatDialog) {
    }

    addPositionsForWinterStorageAssignment(winterStorageAssignment: WinterStorageBoatAssignment): Observable<any> {

        return this._matDialog.open(WinterStorageAssignmentInvoicePositionsComponent, {
            data: {
                winterStorageAssignment
            }
        }).afterClosed()
            .pipe(
                mergeMap((data: WinterStorageBillingPayload & { winterStorageAssignment?: WinterStorageBoatAssignment }) => {
                    if (!data?.positions?.length) {
                        return EMPTY;
                    }
                    return this.persistPositionsForWinterStorageBilling(data);
                })
            );

    }

    persistPositionsForWinterStorageBilling(data: WinterStorageBillingPayload): Observable<InvoicePosition[]> {
        if (data?.person) {
            return forkJoin(data.positions.map(position => {
                applyInvoicePositionBoatForSave(position);
                return this._invoicePositionApiService.addPositionForPerson(position, data.person);
            }));
        }
        if (data?.company) {
            return forkJoin(data.positions.map(position => {
                applyInvoicePositionBoatForSave(position);
                return this._invoicePositionApiService.addPositionForCompany(position, data.company);
            }));
        }
        return EMPTY;
    }

    addPositionsForPerson(person: Person, boat?: Boat): Observable<InvoicePosition[]> {
        return this
            .getInvoicePosition(
                {person, boat, preselection: {boat}},
                undefined,
                buildAreaQuantityButtons({boats: boatsInScope(boat, person?.boats)})
            )
            .pipe(switchMap((positions) => {
                if (positions.length > 0) {
                    return forkJoin(positions.map(position => {
                        applyInvoicePositionBoatForSave(position, boat);
                        return this._invoicePositionApiService.addPositionForPerson(position, person);
                    }));
                }
                return EMPTY;
            }));
    }

    addPositionsForMultiplePersons(persons: Person[]): Observable<InvoicePosition[]> {
        return this
            .getInvoicePosition(undefined, undefined, undefined, undefined, undefined, undefined, undefined, true)
            .pipe(
                switchMap((positions) => {
                    if (positions.length > 0) {
                        const requests = persons.map(person => {
                            return forkJoin(
                                positions.map(position =>
                                    this._invoicePositionApiService.addPositionForPerson(position, person)
                                )
                            );
                        });
                        return forkJoin(requests).pipe(concatAll());
                    }
                    return EMPTY;
                })
            );
    }

    addPositionsForCompany(company: Company, boat?: Boat): Observable<InvoicePosition[]> {
        return this
            .getInvoicePosition(
                {company, boat, preselection: {boat}},
                undefined,
                buildAreaQuantityButtons({boats: boatsInScope(boat, company?.boats)})
            )
            .pipe(switchMap((positions) => {
                if (positions.length > 0) {
                    return forkJoin(positions.map(position => {
                        applyInvoicePositionBoatForSave(position, boat);
                        return this._invoicePositionApiService.addPositionForCompany(position, company);
                    }));
                }
                return EMPTY;
            }));
    }

    addPositionsForMultipleCompanies(companies: Company[]): Observable<InvoicePosition[]> {
        return this
            .getInvoicePosition(undefined, undefined, undefined, undefined, undefined, undefined, undefined, true)
            .pipe(
                switchMap((positions) => {
                    if (positions.length > 0) {
                        const requests = companies.map(company => {
                            return forkJoin(
                                positions.map(position =>
                                    this._invoicePositionApiService.addPositionForCompany(position, company)
                                )
                            );
                        });
                        return forkJoin(requests).pipe(concatAll());
                    }
                    return EMPTY;
                })
            );
    }

    updatePositionForPerson(person: Person, position: InvoicePosition, boat?: Boat): Observable<InvoicePosition[]> {
        return this
            .editInvoicePosition(position, undefined, boatsInScope(boat, person?.boats))
            .pipe(switchMap(editedPositions => {
                if (editedPositions.length > 0) {
                    return forkJoin(editedPositions.map(p => {
                        applyInvoicePositionBoatForSave(p, boat ?? storedInvoicePositionBoat(position));
                        if (p.id === null) {
                            return this._invoicePositionApiService.addPositionForPerson(p, person);
                        } else {
                            return this._invoicePositionApiService.updatePositionForPerson(p.id, p, person);
                        }
                    }));
                }
                return EMPTY;
            }));
    }

    updatePositionForCompany(company: Company, position: InvoicePosition, boat?: Boat): Observable<InvoicePosition[]> {
        return this
            .editInvoicePosition(position, undefined, boatsInScope(boat, company?.boats))
            .pipe(switchMap(editedPositions => {
                if (editedPositions.length > 0) {
                    return forkJoin(editedPositions.map(p => {
                        applyInvoicePositionBoatForSave(p, boat ?? storedInvoicePositionBoat(position));
                        if (p.id === null) {
                            return this._invoicePositionApiService.addPositionForCompany(p, company);
                        } else {
                            return this._invoicePositionApiService.updatePositionForCompany(p.id, p, company);
                        }
                    }));
                }
                return EMPTY;
            }));
    }

    deletePosition(position: InvoicePosition): Observable<any> {
        return this._invoicePositionApiService.deletePosition(position);
    }

    deletePositions(idList: number[]): Observable<any> {
        return this._invoicePositionApiService.deletePositions(idList);
    }

    public getInvoicePosition(dynamicPriceContext?: BcmDynamicPriceContext,
                              productsFilterFn?: (product: Product) => boolean,
                              quantityButtons?: ProduktQuantityButton[],
                              startDate?: Date,
                              endDate?: Date,
                              berthAssignments?: BerthBoatAssignment[],
                              costCenter?: BcmCostCenter,
                              disableVoucherAndDynPrice?: boolean): Observable<InvoicePosition[]> {
        return this._matDialog.open(InvoicePositionDialogComponent, {
            data: {
                dynamicPriceContext,
                productsFilterFn,
                quantityButtons,
                startDate,
                endDate,
                berthAssignments,
                costCenter,
                disableVoucherAndDynPrice
            }
        }).afterClosed();
    }

    public editInvoicePosition(position: InvoicePosition,
                               berthAssignments?: BerthBoatAssignment[],
                               contextBoats?: Boat[],
                               dialogOptions?: {
                                   focusQuantityOnOpen?: boolean;
                                   saveOnEnter?: boolean;
                               }): Observable<InvoicePosition[]> {
        // Preselect the position's boat so editing from a boat context (Bierdeckel)
        // doesn't ask the user to pick a boat again for the dynamic price.
        const boat = position?.boat || position?.dynamicPrice?.boat;

        const dynamicPriceContext: BcmDynamicPriceContext = boat?.id
            ? {
                person: position?.person,
                company: position?.company,
                boat,
                preselection: {boat},
            }
            : undefined;

        return this._matDialog
            .open(InvoicePositionDialogComponent, {
                data: {
                    position,
                    berthAssignments,
                    dynamicPriceContext,
                    // A saved position does not carry its boat back from the API, so the area chips
                    // fall back to the boats the calling screen knows about. (#6944)
                    quantityButtons: buildAreaQuantityButtons({
                        boats: boatsInScope(boat, contextBoats),
                        berths: (berthAssignments || []).map(assignment => assignment.berth),
                    }),
                    focusQuantityOnOpen: dialogOptions?.focusQuantityOnOpen,
                    saveOnEnter: dialogOptions?.saveOnEnter,
                }
            })
            .afterClosed();
    }

}

/**
 * The boat the position is being captured for comes first; the owner's remaining boats follow, so a
 * person's Bierdeckel can still bill an m² product for any of them. Deduplicated by id - the given
 * boat is usually one of the owner's. (#6944)
 */
function boatsInScope(boat: Boat | undefined, ownedBoats: Boat[] | undefined): Boat[] {
    const boats = [boat, ...(ownedBoats || [])].filter(Boolean);

    return boats.filter((candidate, index) =>
        boats.findIndex(other => other.id === candidate.id) === index);
}
